import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, FileStack, Globe2 } from "lucide-react";

import MouRoundGrid from "./MouRoundGrid.jsx";
import MouRoundStepsPanel from "./MouRoundStepsPanel.jsx";
import { DetailSkeleton } from "../../components/Skeleton.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import MouPrintFormsDialog from "./print/MouPrintFormsDialog.jsx";
import { listMouCases, createMouCase, deleteMouCase } from "./mouCaseApi.js";
import { MOU_CASE_STATUS_OPEN, describeMouError } from "./mouCaseShared.js";
import "./mouCase.css";

const DIALOG_TITLE_ID = "wx-mou-rounds-title";
const EMPTY_FORM = { countryCode: "", employerName: "", originCompanyName: "", workerCountRequested: "", jobTypeLabel: "" };

/**
 * P164-R1: every MOU round a recruitment company is running, as a full-screen
 * dialog.
 *
 * Owner: "ทำแบบนี้ใช้ยากมากครับ ... เมื่อกดที่ปุ่มของเมนูด้านขวานี้และเปิด dialog
 * ขึ้นมา (ยึด dialog ของ userprofile) แสดงเป็น grid ของ profile แต่ละรอบ ...
 * เมื่อกดเข้าไปก็จะเข้าไปทำชุดข้อมูลในการส่ง mou".
 *
 * The first cut put the whole thing inside the company drawer: a list, a create
 * form and an eight-step process crammed into a ~440px column. That was the
 * complaint, and it was right - the form alone filled the column, so a company
 * with three rounds could not see them and fill one in at the same time.
 *
 * This is modelled on the Detailed Worker Profile dialog, as instructed: the
 * same near-full-bleed surface, the same two-card composition (a fixed identity
 * band over a body card), the same light backdrop.
 *
 * ONE dialog, two views. Opening a round swaps this dialog's BODY from the grid
 * to that round's steps rather than stacking a second modal on top - two
 * overlays for one task means two Escape targets and a scrim over a scrim, and
 * the way back becomes a dismissal instead of a visible control. The header
 * stays put across both views so "which company am I in" never scrolls away.
 */
export default function MouRoundsDialog({ open, plcOrganizationId, plcName, onClose, onChanged }) {
  const dialogRef = useRef(null);
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  // "grid" | "create" | a round id. One value, so the dialog can only ever be
  // in one of the three - which is what stops a half-typed create form
  // surviving underneath an opened round.
  const [view, setView] = useState("grid");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitState, setSubmitState] = useState({ status: "idle", error: null });
  // The round the operator has asked to delete, held until they answer. Holding
  // the ROUND rather than a boolean is what lets the question name it.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteState, setDeleteState] = useState({ status: "idle", error: null });
  // P164-R5: the round whose government forms are open for printing. A round id
  // rather than a boolean, so the print view can be opened from the grid (where
  // there are many rounds) and from inside one without a second piece of state.
  const [printingCaseId, setPrintingCaseId] = useState(null);

  const load = useCallback(() => {
    if (!open || !plcOrganizationId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    listMouCases(plcOrganizationId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          // Keep whatever was already loaded. A refresh that fails must not
          // throw away a grid the operator is looking at - that turned one
          // transient error into "ไม่สามารถอ่านรายการรอบการยื่น MOU ได้" with no
          // way back except closing the dialog.
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, plcOrganizationId]);

  useEffect(() => load(), [load]);

  // A fresh open must never inherit the last session's view or typing.
  useEffect(() => {
    if (!open) {
      setState({ status: "idle", data: null, error: null });
      setView("grid");
      setForm(EMPTY_FORM);
      setSubmitState({ status: "idle", error: null });
      setPendingDelete(null);
      setDeleteState({ status: "idle", error: null });
      setPrintingCaseId(null);
    }
  }, [open]);

  /**
   * Escape steps BACK one level before it closes: from a round to the grid,
   * from the create form to the grid, and only from the grid out of the dialog.
   * Closing the whole thing from inside a round would throw away the operator's
   * place in a company that might be running twenty of them.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      // The company drawer underneath listens for Escape too and closes on it.
      // Without consuming the event here, one press would tear down the drawer,
      // this dialog and the operator's place all at once.
      event.stopImmediatePropagation();
      // While a confirmation is open it owns Escape - it consumes the event on
      // the capture phase before this handler ever sees it, so there is nothing
      // to do here but stay out of the way.
      if (pendingDelete || printingCaseId) {
        return;
      }
      if (view === "grid") {
        onClose?.();
      } else {
        setView("grid");
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, view, pendingDelete, printingCaseId, onClose]);

  /**
   * Opening the create form seeds the origin-country agency with the company
   * this dialog was opened from. Owner: "บริษัทจัดหางานประเทศต้นทาง ให้ default
   * บริษัทที่ click ขึ้นมาเลย" - it is a starting value, not a fixed one, so a
   * round whose origin agency is genuinely a different company is one edit away.
   */
  function openCreate() {
    setForm({ ...EMPTY_FORM, originCompanyName: list?.plcName ?? plcName ?? "" });
    setSubmitState({ status: "idle", error: null });
    setView("create");
  }

  function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    setDeleteState({ status: "deleting", error: null });
    deleteMouCase(plcOrganizationId, pendingDelete.mouCaseId)
      .then((refreshedList) => {
        // The server hands back the list it now holds, so the grid is correct
        // without a second read that could disagree with it.
        setState({ status: "ready", data: refreshedList, error: null });
        setPendingDelete(null);
        setDeleteState({ status: "idle", error: null });
        onChanged?.();
      })
      .catch((error) => {
        setDeleteState({
          status: "idle",
          error: describeMouError(error, "ลบรอบนี้ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"),
        });
      });
  }

  function submitCreate() {
    const employerName = form.employerName.trim();
    if (!employerName) {
      setSubmitState({ status: "error", error: "กรุณาระบุชื่อนายจ้าง/สถานประกอบการที่ยื่นเรื่องให้" });
      return;
    }

    setSubmitState({ status: "submitting", error: null });
    const countryCode = form.countryCode || availableCountries[0]?.code || null;
    createMouCase(plcOrganizationId, { ...form, employerName, countryCode })
      .then((created) => {
        setSubmitState({ status: "idle", error: null });
        setForm(EMPTY_FORM);
        load();
        onChanged?.();
        // Straight into the new round: it was opened to start filing step 1,
        // not to be admired in a grid.
        setView(created.mouCaseId);
      })
      .catch((error) => {
        setSubmitState({ status: "error", error: describeMouError(error, "ไม่สามารถเปิดรอบใหม่ได้ กรุณาลองใหม่อีกครั้ง") });
      });
  }

  const list = state.data;
  const rounds = useMemo(() => list?.items ?? [], [list]);
  const canWrite = list?.canWrite ?? false;
  const availableCountries = useMemo(() => list?.availableCountries ?? [], [list]);
  const openRoundCount = useMemo(() => rounds.filter((round) => round.status === MOU_CASE_STATUS_OPEN).length, [rounds]);

  if (!open || !plcOrganizationId) {
    return null;
  }

  const inRound = view !== "grid" && view !== "create";

  return (
    <>
    <div
      className="wx-mou-dialog-root"
      // mousedown, not click: a drag that starts inside the dialog and ends
      // outside it (selecting text in a note) must not close it.
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose?.();
        }
      }}
    >
      <div className="wx-mou-dialog-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-mou-dialog" role="dialog" aria-modal="true" aria-labelledby={DIALOG_TITLE_ID}>
        {inRound ? (
          // The round's own panel brings its own header band and title, so the
          // grid header steps aside entirely rather than stacking two.
          <MouRoundStepsPanel
            plcOrganizationId={plcOrganizationId}
            mouCaseId={view}
            titleId={DIALOG_TITLE_ID}
            onPrint={() => setPrintingCaseId(view)}
            onBack={() => {
              // Only this dialog reloads. The drawer summary behind it is
              // refreshed when the dialog closes - firing both here meant two
              // simultaneous reads of the same endpoint on every back press,
              // and either one failing showed an error over a working grid.
              setView("grid");
              load();
            }}
            onChanged={onChanged}
          />
        ) : (
          <>
            <header className="wx-mou-head">
              <p className="wx-mou-kicker">
                <FileStack size={16} aria-hidden="true" />
                การยื่น MOU
              </p>
              <div className="wx-mou-head-main">
                <div className="wx-mou-head-id">
                  <h2 id={DIALOG_TITLE_ID} className="wx-mou-title">
                    {list?.plcName ?? plcName ?? "บริษัทจัดหางาน"}
                  </h2>
                  <p className="wx-mou-sub">แต่ละรอบคือการขอแรงงาน 1 ชุด ให้นายจ้าง 1 ราย จาก 1 ประเทศ</p>
                  <div className="wx-mou-pills">
                    <span className="wx-mou-pill">
                      <FileStack size={13} aria-hidden="true" />
                      {list?.totalCount ?? 0} รอบทั้งหมด
                    </span>
                    <span className="wx-mou-pill">
                      <Globe2 size={13} aria-hidden="true" />
                      กำลังดำเนินการ {openRoundCount} รอบ
                    </span>
                  </div>
                </div>

                <div className="wx-mou-head-aside">
                  <button type="button" className="wx-mou-close" onClick={onClose} aria-label="ปิดหน้าต่างการยื่น MOU">
                    <X size={17} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </header>

            <div className="wx-mou-body">
              <div className="wx-mou-scroll">
                {state.status === "error" && !list ? (
                  <div className="wx-mou-grid-empty" role="alert">
                    <p className="wx-mou-grid-empty-title">ไม่สามารถอ่านรายการรอบการยื่น MOU ได้</p>
                    <p className="wx-mou-grid-empty-body">{describeMouError(state.error, "กรุณาปิดหน้าต่างแล้วลองใหม่อีกครั้ง")}</p>
                    <button type="button" className="wx-button wx-button--primary" onClick={load}>
                      ลองใหม่อีกครั้ง
                    </button>
                  </div>
                ) : state.status === "loading" && state.data === null ? (
                  <DetailSkeleton label="กำลังโหลดรายการรอบการยื่น MOU..." sections={3} />
                ) : view === "create" ? (
                  <section className="wx-mou-create-panel">
                    <div className="wx-mou-panel-head">
                      <div>
                        <p className="wx-mou-panel-kicker">รอบที่ {(list?.totalCount ?? 0) + 1}</p>
                        <h3 className="wx-mou-panel-title">เปิดรอบการยื่น MOU ใหม่</h3>
                        <p className="wx-mou-panel-summary">
                          กรอกเท่าที่รู้ตอนนี้ก็พอ — ที่เหลือกรอกได้ทีละขั้นตอนตอนที่มีเอกสารจริงในมือ
                        </p>
                      </div>
                    </div>

                    <div className="wx-mou-fields">
                      <label className="wx-mou-field wx-mou-field--wide">
                        <span>
                          นายจ้าง/สถานประกอบการ <b>*</b>
                        </span>
                        <input
                          type="text"
                          aria-label="นายจ้าง/สถานประกอบการ"
                          maxLength={200}
                          placeholder="ชื่อบริษัทที่ขอแรงงานชุดนี้"
                          value={form.employerName}
                          onChange={(event) => setForm((previous) => ({ ...previous, employerName: event.target.value }))}
                        />
                      </label>

                      {/* Options come from the server, never from a list in this
                          bundle: the picker has to offer exactly what the server
                          will accept, or it becomes a set of choices that 400 on
                          submit. Fixed at open time because every step after
                          this one is filed with that country's own offices. */}
                      <label className="wx-mou-field">
                        <span>
                          ประเทศต้นทาง <b>*</b>
                        </span>
                        <select
                          aria-label="ประเทศต้นทาง"
                          value={form.countryCode || availableCountries[0]?.code || ""}
                          onChange={(event) => setForm((previous) => ({ ...previous, countryCode: event.target.value }))}
                        >
                          {availableCountries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="wx-mou-field">
                        <span>จำนวนแรงงานที่ขอ (คน)</span>
                        <input
                          type="number"
                          aria-label="จำนวนแรงงานที่ขอ"
                          min={1}
                          value={form.workerCountRequested}
                          onChange={(event) => setForm((previous) => ({ ...previous, workerCountRequested: event.target.value }))}
                        />
                      </label>

                      <label className="wx-mou-field">
                        <span>ประเภทงาน</span>
                        <input
                          type="text"
                          aria-label="ประเภทงาน"
                          maxLength={120}
                          placeholder="เช่น งานกรรมกร"
                          value={form.jobTypeLabel}
                          onChange={(event) => setForm((previous) => ({ ...previous, jobTypeLabel: event.target.value }))}
                        />
                      </label>

                      <label className="wx-mou-field wx-mou-field--wide">
                        <span>บริษัทจัดหางานประเทศต้นทาง</span>
                        <input
                          type="text"
                          aria-label="บริษัทจัดหางานประเทศต้นทาง"
                          maxLength={200}
                          placeholder="ชื่อบริษัทที่ได้รับมอบอำนาจในเมียนมา"
                          value={form.originCompanyName}
                          onChange={(event) => setForm((previous) => ({ ...previous, originCompanyName: event.target.value }))}
                        />
                      </label>
                    </div>

                    {submitState.error && (
                      <p className="wx-mou-error" role="alert">
                        {submitState.error}
                      </p>
                    )}

                    <div className="wx-mou-panel-actions">
                      <button
                        type="button"
                        className="wx-button wx-button--ghost"
                        disabled={submitState.status === "submitting"}
                        onClick={() => {
                          setView("grid");
                          setSubmitState({ status: "idle", error: null });
                        }}
                      >
                        ยกเลิก
                      </button>
                      <button
                        type="button"
                        className="wx-button wx-button--primary"
                        disabled={submitState.status === "submitting"}
                        onClick={submitCreate}
                      >
                        {submitState.status === "submitting" ? "กำลังเปิดรอบ..." : "เปิดรอบและเริ่มขั้นตอนที่ 1"}
                      </button>
                    </div>
                  </section>
                ) : (
                  <>
                    {state.status === "error" && (
                      <p className="wx-mou-error" role="alert">
                        {describeMouError(state.error, "อัปเดตรายการรอบล่าสุดไม่สำเร็จ กำลังแสดงข้อมูลที่โหลดไว้ก่อนหน้า")}
                      </p>
                    )}
                    <MouRoundGrid
                    rounds={rounds}
                    canWrite={canWrite}
                    onOpen={(mouCaseId) => setView(mouCaseId)}
                    onCreate={openCreate}
                    onPrint={(round) => setPrintingCaseId(round.mouCaseId)}
                    onRequestDelete={(round) => {
                      setDeleteState({ status: "idle", error: null });
                      setPendingDelete(round);
                    }}
                  />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Deliberately a SIBLING of the dialog root, not a child. That root closes
        on any mousedown that lands outside its panel - so a confirmation
        rendered inside it turned every press of its own buttons into "you
        clicked outside", tearing down the dialog before the answer was read.
        It is position:fixed at a higher layer, so its place in the DOM costs it
        nothing visually. */}
    <ConfirmDialog
        open={pendingDelete !== null}
        title="ลบรอบการยื่น MOU นี้?"
        body="รอบนี้จะหายไปจากรายการ พร้อมกับขั้นตอนและเอกสารทั้งหมดที่แนบไว้ในรอบนั้น การลบนี้ย้อนกลับจากหน้าจอไม่ได้"
        detail={
          pendingDelete && (
            <>
              <strong>
                รอบที่ {pendingDelete.roundNumber} · {pendingDelete.employerName || "ยังไม่ระบุนายจ้าง"}
              </strong>
              <span>
                {pendingDelete.countryLabel} · {pendingDelete.caseCode} · ทำไปแล้ว {pendingDelete.completedStepCount} จาก{" "}
                {pendingDelete.stepCount} ขั้นตอน · เอกสาร {pendingDelete.documentCount} ไฟล์
              </span>
            </>
          )
        }
        confirmLabel="ลบรอบนี้"
        cancelLabel="ไม่ลบ"
        tone="danger"
        busy={deleteState.status === "deleting"}
        error={deleteState.error}
        onConfirm={confirmDelete}
      onCancel={() => {
        setPendingDelete(null);
        setDeleteState({ status: "idle", error: null });
      }}
    />

    {/* Also a SIBLING, for the same reason as the confirmation above: rendered
        inside the dialog root, its own toolbar presses would read as "you
        clicked outside" and close the dialog underneath it. */}
    <MouPrintFormsDialog
      open={printingCaseId !== null}
      plcOrganizationId={plcOrganizationId}
      mouCaseId={printingCaseId}
      onClose={() => setPrintingCaseId(null)}
    />
    </>
  );
}
